import { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";
import { CommonImages } from "../../../../reusableUI/images";
import {
  addBlockedNumber,
  getBlockedNumbersList,
  removeBlockedNumber,
} from "../../../../Service/blockedNumbers";
import { getLookupData } from "../../../../Service/lookup";
import Loader from "../../../../reusableUI/loader";
import { showToast } from "../../../../reusableUI/toaster";
import {
  capitalizeFirstLetter,
  formatPhoneNumber,
} from "../../../../utils/Functions";
import moment from "moment";
import { IoIosAddCircleOutline } from "react-icons/io";
import { FiTrash2 } from "react-icons/fi";

const BlockedNumbers = ({ searchTerm }) => {
  const [loading, setLoading] = useState(false);
  const [blockedList, setBlockedList] = useState([]);
  const [paginationPage, setPaginationPage] = useState(0);
  const [phoneCode, setPhoneCode] = useState("");
  const [number, setNumber] = useState("");
  const [lookup, setLookup] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadLookup();
    loadList();
  }, []);

  const loadLookup = async () => {
    try {
      const result = await getLookupData();
      if (result.error === false) {
        setLookup(result.data || []);
      } else {
        console.log("Error fetching lookup data");
      }
    } catch (e) {
      console.error("Lookup error:", e);
    }
  };

  const loadList = async () => {
    try {
      setLoading(true);
      const result = await getBlockedNumbersList();
      if (result.error === false) {
        setBlockedList(result.data || []);
      } else {
        console.log("Error fetching blocked numbers");
      }
    } catch (e) {
      console.error("List error:", e);
    } finally {
      setLoading(false);
    }
  };

  const phoneCodeOptions = useMemo(
    () => lookup.filter((item) => item.type === "phonecode"),
    [lookup]
  );

  const handleAdd = async () => {
    if (!phoneCode) {
      showToast("Please select a country code.", "Error");
      return;
    }
    if (!number?.trim()) {
      showToast("Please enter a phone number.", "Error");
      return;
    }

    const payload = {
      phone: `${phoneCode}${number.trim()}`,
    };

    try {
      setLoading(true);
      const req = await addBlockedNumber(payload);

      if (req.error === false) {
        showToast("Number blocked successfully.", "Success");
        setPhoneCode("");
        setNumber("");
        setIsModalOpen(false);
        loadList();
      } else {
        showToast(req.message || "Failed to block number.", "Error");
      }
    } catch (err) {
      console.error("Add Error:", err);
      showToast("Something went wrong.", "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    console.log(id);

    const confirmDelete = window.confirm(
      "Are you sure you want to remove this blocked number?"
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const req = await removeBlockedNumber(id);

      if (req.error === false) {
        showToast("Blocked number removed successfully.", "Success");
        loadList();
      } else {
        showToast(
          "Something went wrong while removing the blocked number.",
          "Error"
        );
      }
    } catch (error) {
      console.error("Error removing blocked number:", error);
      showToast("An error occurred. Please try again.", "Error");
    }
  };

  console.log(blockedList);

  const columns = useMemo(
    () => [
      {
        Header: () => <div className="">S.No</div>,
        id: "sno",
        Cell: ({ row }) => (
          <div className="ml-2 text-[#504EFF] text-sm">{row.index + 1}</div>
        ),
      },
      {
        Header: () => <div className="">Contact No.</div>,
        id: "phoneDisplay",
        Cell: ({ row: { original } }) => {
          const full = original?.phone;
          return (
            <p className=" text-[#504EFF] font-medium text-[12px]">
              {formatPhoneNumber(full)}
            </p>
          );
        },
      },
      {
        Header: () => <div className="">Since</div>,
        accessor: "date",
        Cell: ({ row: { original } }) => (
          <p className="text-gray-500 text-[12px]">
            {original?.date
              ? moment(original.date).format("DD-MM-YYYY hh:mm:ss A")
              : "—"}
          </p>
        ),
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row: { original } }) => (
          <div className="flex items-center  ml-3">
            <FiTrash2
              className="text-red-500 cursor-pointer"
              size={14}
              onClick={() => handleDelete(original._id)}
            />
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { pageIndex, sortBy: currentSortBy },
  } = useTable(
    {
      columns,
      data: blockedList,
      initialState: {
        pageIndex: paginationPage,
      },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    localStorage.setItem("sortBy", JSON.stringify(currentSortBy));
  }, [currentSortBy]);

  useEffect(() => {
    if (pageIndex !== paginationPage) {
      setPaginationPage(pageIndex);
    }
  }, [pageIndex]);

  return (
    <div>
      {loading && (
        <div>
          <Loader loading={loading} />
        </div>
      )}

      <div className="bg-[#F3F6F5] ">
        <div className="w-[98%] bg-white rounded-2xl overflow-hidden h-[87vh] shadow-lg mt-2 m-2">
          <div className="flex justify-between items-center mt-[-25px] p-2">
            <div className="flex flex-row items-center mt-4">
              <p className="text-[14px] font-bold ml-2 mt-4">
                Blocked Users ({blockedList.length})
              </p>
            </div>

            <button
              type="button"
              className="flex flex-row items-center bg-[white]  gap-3 p-2 rounded-md cursor-pointer m-2 mt-8 border border-[#504EFF] hover:bg-gray-100"
              onClick={() => setIsModalOpen(true)}
            >
              <span className="text-[12px] text-[#504EFF] font-semibold">
                Block User
              </span>
              <IoIosAddCircleOutline color="#504EFF" size={18} />
            </button>
          </div>

          <div className="overflow-x-auto max-h-[72vh]">
            <div className="min-w-full w-[100%]">
              <ReusableTable
                columns={columns}
                getTableProps={getTableProps}
                getTableBodyProps={getTableBodyProps}
                headerGroups={headerGroups}
                tablePage={tablePage}
                prepareRow={prepareRow}
                filterIcon={CommonImages.filter}
                searchTerm={searchTerm}
              />
            </div>
          </div>
          <div className="mb-2">
            <Page
              paginationPage={paginationPage}
              nextPage={nextPage}
              previousPage={previousPage}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              pageOptions={pageOptions}
            />
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setIsModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Block Number Modal"
        >
          <div
            className="bg-white  max-w-md rounded-md shadow-xl p-5 border border-[#9EA3FF] h-[32%]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[14px] text-[#504EFF] font-semibold">
                Blocked User
              </h3>
              <button
                className="text-[#504EFF] hover:text-gray-700 text-sm"
                onClick={() => setIsModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <form
              className="flex flex-col gap-8"
              onSubmit={(e) => {
                e.preventDefault();
                handleAdd();
              }}
            >
              <div className="flex flex-col min-w-[280px]">
                <div className="flex items-center gap-4 mb-1">
                  <label className="text-[12px] font-medium text-gray-700 ">
                    Mobile Number
                    <span className="text-red-600  text-[18px]">*</span>
                  </label>
                </div>
                <div className="flex">
                  <div className="relative w-[70px]">
                    <select
                      className="appearance-none w-full border border-[#D2D2D2] px-2 py-1.5 rounded-sm text-[12px] text-[#606060] font-poppins"
                      value={phoneCode}
                      onChange={(e) => setPhoneCode(e.target.value)}
                    >
                      <option hidden></option>
                      {phoneCodeOptions.map((item, index) => (
                        <option
                          key={`${item.value}-${index}`}
                          value={item.value}
                        >
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center justify-center mr-2">
                      <svg
                        className="w-4 h-4 text-[#606060]"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.25 4.66a.75.75 0 01-1.08 0l-4.25-4.66a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  <input
                    type="text"
                    className="border border-[#D2D2D2] w-[240px] px-2 py-1 rounded-r-sm text-[12px]"
                    placeholder="Enter mobile number"
                    value={number}
                    maxLength={10}
                    onChange={(e) =>
                      setNumber(e.target.value.replace(/\D/g, ""))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-center gap-2 mt-2">
                <button
                  type="submit"
                  className="px-5 py-2 text-[12px] rounded-md text-white bg-gradient-to-r from-blue-500 to-blue-700 hover:opacity-90 disabled:opacity-60"
                  disabled={!phoneCode || !number || loading}
                >
                  Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockedNumbers;
